import type { CourseRegistration, CourseRegistrationStatus, MindItem } from '@kimuno/api-sdk';
import immutable from 'object-path-immutable';

import { LOGOUT, type LogoutAction } from '../actions/auth.actionTypes';
import { AsyncActionStatus } from '../actions/common.actionTypes';
import {
	CREATE_COURSE_REGISTRATION,
	type CreateCourseRegistrationAction,
	FETCH_ALL_USER_COURSES,
	FETCH_PROGRESS_USER_COURSES,
	FETCH_SUCCESS_USER_COURSES,
	type FetchAllUserCoursesAction,
	type FetchProgressUserCoursesAction,
	type FetchSuccessUserCoursesAction,
	GET_CERTIFIED_COURSE_REGISTRATIONS,
	type GetCertifiedCourseRegistrationsAction,
	UPDATE_COURSE_REGISTRATION,
	type UpdateCourseRegistrationAction,
} from '../actions/course.actionTypes';
import {
	PIN_MIND_ITEM,
	type PinMindItemAction,
	UNPIN_DOCUMENT,
	type UnpinDocumentAction,
} from '../actions/pinned.actionTypes';
import { searchSettings } from '../constants';
import type { Pagination } from '../types/search';
import { arrayToObject } from '../utils/arrayToObject';
import type { StoreItem } from '.';

export type CourseMindItemPage = StoreItem<string[]>;

export interface CourseState {
	certifiedCourseRegistrations: {
		byMindItemId: {
			[key: string]: CourseRegistration;
		};
		status: AsyncActionStatus;
		allLoaded?: boolean;
	};
	courseRegistrations: {
		byMindItemId: {
			[key: string]: CourseRegistration;
		};
	};
	allCourses: {
		pages: CourseMindItemPage[];
		pagination: Pagination;
		byId: {
			[key: string]: MindItem<'course'>;
		};
	};
	progressCourses: {
		pages: CourseMindItemPage[];
		pagination: Pagination;
		byId: {
			[key: string]: MindItem<'course'>;
		};
	};
	successCourses: {
		pages: CourseMindItemPage[];
		pagination: Pagination;
		byId: {
			[key: string]: MindItem<'course'>;
		};
	};
}

const initialPagination: Pagination = {
	total: -1,
	limit: searchSettings.itemsPerPage,
	offset: 0,
};

const getStoreKey = (registrationStatus?: CourseRegistrationStatus) => {
	switch (registrationStatus) {
		case 'progress':
			return 'progressCourses';
		case 'success':
			return 'successCourses';
		default:
			return 'allCourses';
	}
};

const initialState: CourseState = {
	certifiedCourseRegistrations: {
		byMindItemId: {},
		status: AsyncActionStatus.INITIAL,
		allLoaded: false,
	},
	courseRegistrations: {
		byMindItemId: {},
	},
	allCourses: {
		pages: [],
		pagination: initialPagination,
		byId: {},
	},
	progressCourses: {
		pages: [],
		pagination: initialPagination,
		byId: {},
	},
	successCourses: {
		pages: [],
		pagination: initialPagination,
		byId: {},
	},
};

type CourseActionTypes =
	| GetCertifiedCourseRegistrationsAction
	| UpdateCourseRegistrationAction
	| FetchAllUserCoursesAction
	| FetchProgressUserCoursesAction
	| FetchSuccessUserCoursesAction
	| PinMindItemAction
	| UnpinDocumentAction
	| CreateCourseRegistrationAction
	| LogoutAction;

// biome-ignore lint/style/useDefaultParameterLast: parameter order is a fixed contract (Redux reducer signature / existing call sites)
const courses = (state = initialState, action: CourseActionTypes) => {
	switch (action.type) {
		case LOGOUT.SUCCESS:
			return initialState;

		case GET_CERTIFIED_COURSE_REGISTRATIONS.START:
			return immutable(state)
				.set(['certifiedCourseRegistrations', 'status'], AsyncActionStatus.LOADING)
				.value();

		case GET_CERTIFIED_COURSE_REGISTRATIONS.ERROR:
			return immutable(state)
				.set(['certifiedCourseRegistrations', 'status'], AsyncActionStatus.ERROR)
				.value();

		case GET_CERTIFIED_COURSE_REGISTRATIONS.SUCCESS: {
			const currentById = state.certifiedCourseRegistrations.byMindItemId;

			const newById = action.result.reduce<{
				[key: string]: CourseRegistration;
			}>((acc, courseRegistration) => {
				if (!currentById[courseRegistration.mindItemId]) {
					acc[courseRegistration.mindItemId] = courseRegistration;
				}
				return acc;
			}, {});

			return immutable(state)
				.set(['certifiedCourseRegistrations', 'byMindItemId'], {
					...currentById,
					...newById,
				})
				.set(['certifiedCourseRegistrations', 'status'], AsyncActionStatus.READY)
				.set(['certifiedCourseRegistrations', 'allLoaded'], true)
				.value();
		}

		case UPDATE_COURSE_REGISTRATION.SUCCESS: {
			const itemId = action.result.mindItemId;
			let newState = immutable(state);
			const isSuccess = action.result.isCompleted && action.result.isPassed;

			if (isSuccess) {
				newState = newState
					.set(['certifiedCourseRegistrations', 'byMindItemId', itemId], action.result)
					.set('allCourses', initialState.allCourses)
					.set('progressCourses', initialState.progressCourses)
					.set('successCourses', initialState.successCourses);
			} else {
				newState = newState
					.set('allCourses', initialState.allCourses)
					.set('progressCourses', initialState.progressCourses);
			}

			return newState.set(['courseRegistrations', 'byMindItemId', itemId], action.result).value();
		}

		case FETCH_ALL_USER_COURSES.START:
		case FETCH_PROGRESS_USER_COURSES.START:
		case FETCH_SUCCESS_USER_COURSES.START: {
			const requestPagination: Pagination = action.payload.pagination;
			const incomingPagesStartIndex: number = Math.floor(
				requestPagination.offset / searchSettings.itemsPerPage
			);
			const incomingPagesEndIndex: number = Math.floor(
				(requestPagination.offset + requestPagination.limit) / searchSettings.itemsPerPage
			);
			const key = getStoreKey(action.payload.registrationStatus);
			// biome-ignore lint/suspicious/noDoubleEquals: loose equality kept; strict equality changes null/undefined coercion semantics
			if (action.payload.pagination.offset == 0) {
				state[key] = initialState[key];
			}

			let updatedState = immutable(state);

			for (let i = incomingPagesStartIndex; i < incomingPagesEndIndex; i++) {
				updatedState = updatedState.set([key, 'pages', i], {
					status: AsyncActionStatus.LOADING,
				});
			}

			return updatedState.value();
		}

		case FETCH_ALL_USER_COURSES.SUCCESS:
		case FETCH_PROGRESS_USER_COURSES.SUCCESS:
		case FETCH_SUCCESS_USER_COURSES.SUCCESS: {
			const requestPagination: Pagination = action.payload.pagination;
			const incomingPagesStartIndex: number = Math.floor(
				requestPagination.offset / searchSettings.itemsPerPage
			);
			const incomingPagesEndIndex: number = Math.floor(
				(requestPagination.offset + requestPagination.limit) / searchSettings.itemsPerPage
			);
			const key = getStoreKey(action.payload.registrationStatus);

			let updatedState = immutable(state);
			for (let i = incomingPagesStartIndex; i < incomingPagesEndIndex; i++) {
				const firstItemIndex = (i - incomingPagesStartIndex) * searchSettings.itemsPerPage;
				const lastItemIndex = (i - incomingPagesStartIndex + 1) * searchSettings.itemsPerPage;

				const pageData = action.result.data.slice(firstItemIndex, lastItemIndex);
				const newPage: CourseMindItemPage = {
					status: AsyncActionStatus.READY,
					data: pageData.map((courseMindItem) => courseMindItem.id),
				};
				updatedState = updatedState.set([key, 'pages', i], newPage);
				updatedState = updatedState.merge(
					[key, 'byId'],
					arrayToObject(pageData, (courseMindItem) => courseMindItem.id)
				);
			}
			return updatedState
				.set([key, 'pagination', 'total'], action.result.meta.pagination.total)
				.set([key, 'pagination', 'offset'], state[key].pagination.offset + requestPagination.limit)
				.value();
		}

		case FETCH_ALL_USER_COURSES.ERROR:
		case FETCH_PROGRESS_USER_COURSES.ERROR:
		case FETCH_SUCCESS_USER_COURSES.ERROR: {
			const requestPagination: Pagination = action.payload.pagination;
			const incomingPageIndex: number = Math.floor(
				requestPagination.offset / searchSettings.itemsPerPage
			);
			const key = getStoreKey(action.payload.registrationStatus);

			return immutable(state)
				.set([key, 'pages', incomingPageIndex], {
					status: AsyncActionStatus.ERROR,
					error: action.error.message,
				})
				.value();
		}

		case PIN_MIND_ITEM.START:
		case UNPIN_DOCUMENT.ERROR: {
			const itemId = action.payload.id;

			const subStores: Array<'allCourses' | 'progressCourses' | 'successCourses'> = [
				'allCourses',
				'progressCourses',
				'successCourses',
			];

			let updatedState = state;
			// biome-ignore lint/suspicious/useIterableCallbackReturn: pre-existing callback shape; changing returns needs per-site behavior review
			subStores.map((subStore) => {
				const byId = updatedState[subStore].byId;

				if (!byId[itemId]) return;

				const newlyPinnedItem = {
					...updatedState[subStore].byId[itemId],
					isPinned: true,
				};
				updatedState = immutable(updatedState)
					.merge([subStore, 'byId', itemId], newlyPinnedItem)
					.value();
			});

			return updatedState;
		}

		case PIN_MIND_ITEM.ERROR:
		case UNPIN_DOCUMENT.START: {
			const itemId = action.payload.id;

			const subStores: Array<'allCourses' | 'progressCourses' | 'successCourses'> = [
				'allCourses',
				'progressCourses',
				'successCourses',
			];

			let updatedState = state;
			subStores.forEach((subStore) => {
				const byId = updatedState[subStore].byId;

				if (!byId[itemId]) return;

				const newlyPinnedItem = {
					...updatedState[subStore].byId[itemId],
					isPinned: false,
				};
				updatedState = immutable(updatedState)
					.merge([subStore, 'byId', itemId], newlyPinnedItem)
					.value();
			});

			return updatedState;
		}

		case CREATE_COURSE_REGISTRATION.SUCCESS:
			return immutable(state)
				.set('allCourses', initialState.allCourses)
				.set('progressCourses', initialState.progressCourses)
				.value();

		default:
			return state;
	}
};

export default courses;
